import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { FaArrowLeft } from 'react-icons/fa';
import Button from '../common/Button';
import ToastContainer from '../common/ToastContainer';
import { useToast } from '../../hooks/useToast';
import { useOrganizationTimezone } from '../../hooks/useOrganizationTimezone';
import SupportCaseService from '../../services/supportCaseService';
import { SUPPORT_CASE_STATUS_LABELS, formatSupportCaseDate } from './supportCaseUtils';
import './Ayuda.css';

const MAX_COMMENT_LENGTH = 5000;

const SupportCaseDetail = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const { timezone } = useOrganizationTimezone();
  const { toasts, removeToast, success: showSuccess, error: showError } = useToast();
  const [supportCase, setSupportCase] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);
  const shownMessageKeyRef = useRef(null);

  const token = auth.user?.access_token;

  useEffect(() => {
    if (!location.state?.message || shownMessageKeyRef.current === location.key) return;
    shownMessageKeyRef.current = location.key;
    showSuccess(location.state.message);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.key, location.pathname, location.state?.message, navigate, showSuccess]);

  useEffect(() => {
    if (!token) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    const loadCase = async () => {
      setIsLoading(true);
      try {
        const result = await SupportCaseService.getCase(caseId, token);
        if (!cancelled) setSupportCase(result.data);
      } catch (err) {
        if (!cancelled) showError(err.message || 'Error al cargar la solicitud');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadCase();
    return () => { cancelled = true; };
  }, [caseId, token, showError]);

  const handleSubmitComment = async (event) => {
    event.preventDefault();
    const body = comment.trim();
    if (!token || !body) return;

    setSending(true);
    try {
      const result = await SupportCaseService.addComment(caseId, body, token);
      setSupportCase((current) => ({
        ...current,
        status: result.data.status,
        comments: [...(current.comments || []), result.data.comment]
      }));
      setComment('');
      showSuccess('Comentario enviado');
    } catch (err) {
      showError(err.message || 'Error al enviar el comentario');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="ayuda-page-container">
      <button type="button" className="ayuda-back-link" onClick={() => navigate('/ayuda')}>
        <FaArrowLeft />
        <span>Volver a Ayuda</span>
      </button>

      {isLoading ? (
        <div className="ayuda-loading">
          <div className="ayuda-spinner"></div>
        </div>
      ) : !supportCase ? (
        <p className="ayuda-empty">No se encontró la solicitud.</p>
      ) : (
        <div className="ayuda-content">
          <div className="ayuda-card">
            <div className="ayuda-case-header">
              <h1>{supportCase.title}</h1>
              <span className={`ayuda-status-badge ayuda-status-${supportCase.status}`}>
                {SUPPORT_CASE_STATUS_LABELS[supportCase.status] || supportCase.status}
              </span>
            </div>
            <p className="ayuda-case-item-meta">
              {supportCase.createdByName && `${supportCase.createdByName} · `}
              {formatSupportCaseDate(supportCase.createdAt, timezone)}
            </p>
            <p className="ayuda-case-description">{supportCase.description}</p>
          </div>

          <section className="ayuda-seccion">
            <h2>Comentarios</h2>
            {supportCase.comments?.length > 0 ? (
              <div className="ayuda-comments">
                {supportCase.comments.map((item) => (
                  <div
                    key={item.id}
                    className={`ayuda-comment ${item.authorType === 'support' ? 'ayuda-comment-support' : ''}`}
                  >
                    <div className="ayuda-comment-header">
                      <span className="ayuda-comment-author">
                        {item.authorType === 'support' ? 'Soporte Dentaris' : item.authorName}
                      </span>
                      <span>{formatSupportCaseDate(item.createdAt, timezone)}</span>
                    </div>
                    <p className="ayuda-comment-body">{item.body}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="ayuda-empty">Aún no hay comentarios.</p>
            )}

            <form className="ayuda-card ayuda-form" onSubmit={handleSubmitComment}>
              <div className="ayuda-field">
                <label className="ayuda-label" htmlFor="support-case-comment">Agregar comentario</label>
                <textarea
                  id="support-case-comment"
                  className="ayuda-textarea"
                  placeholder="Escribe información adicional o responde al equipo de soporte"
                  value={comment}
                  onChange={(event) => setComment(event.target.value)}
                  maxLength={MAX_COMMENT_LENGTH}
                  rows={4}
                  disabled={sending}
                />
                {supportCase.status !== 'open' && (
                  <span className="ayuda-field-hint">Al comentar, la solicitud se volverá a abrir.</span>
                )}
              </div>
              <div className="ayuda-actions">
                <Button type="submit" variant="primary" icon="fas fa-paper-plane" loading={sending} disabled={!comment.trim()}>
                  Enviar comentario
                </Button>
              </div>
            </form>
          </section>
        </div>
      )}

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default SupportCaseDetail;
